import * as React from "react";
import { Modal, Button, Form, Confirm } from "semantic-ui-react";

import { connect } from "react-redux";
import { ThunkDispatch } from "redux-thunk";

import { Input } from "@neworbit/simpleui-input";

import { navigateToParentRoute } from "@teamprevent/common/routing/actions";
import { IsLoadingButton } from "@teamprevent/common/IsLoadingButton";
import { toast } from "@teamprevent/common/toasts";
import { usePrevious } from "@teamprevent/common/customHooks";

import { ApplicationState } from "../../applicationState";
import { routeIdSelector as customerIdSelector } from "../../crud/customer/selectors";
import { routeIdSelector as employeeRecordIdSelector } from "../../crud/employeeRecord/selectors";
import { createHealthSurveillanceCase } from "../actions";
import { OhCaseDetailModel } from "../../crud/ohCase/model";
import { CREATE_HEALTH_SURVEILLANCE_CASE } from "../actionTypes";
import { caseTypeOptions, HealthSurveillanceCaseType } from "../models";

import { push } from "redux-little-router";

interface StateProps {
    customerId: string;
    employeeRecordId: string;
}

interface DispatchProps {
    navigateToParent: () => void;
    createHealthSurveillanceCase: (customerId: string, employeeId: string, caseTypes: HealthSurveillanceCaseType[]) => void;
}

interface Props {
    navigateToParent: () => void;
    createHealthSurveillanceCase: (caseTypes: HealthSurveillanceCaseType[]) => void;
}

const healthSurveillanceAndFitForWorkCaseTypeOptionsValue = caseTypeOptions.find(cto => cto.value === "Both").value;

const CreateHealthSurveillanceCaseModalUnconnected: React.FunctionComponent<Props> = (props: Props) => {
    const [caseTypes, setCaseTypes] = React.useState<string>(healthSurveillanceAndFitForWorkCaseTypeOptionsValue);
    const [submitted, setSubmitted] = React.useState<boolean>(false);
    const [confirmationModalOpen, setConfirmationModalOpen] = React.useState<boolean>(false);
    const previousCaseTypes = usePrevious(caseTypes);

    const onChangeCaseType = (t: string) => {
        if (t !== healthSurveillanceAndFitForWorkCaseTypeOptionsValue) {
            setConfirmationModalOpen(true);
        }
        setCaseTypes(t);
    };

    const handleCreateHealthSurveillanceCase = () => {
        setSubmitted(true);
        if (!caseTypes) {
            return;
        }
        try {
            props.createHealthSurveillanceCase(caseTypeOptions.find(o => o.value === caseTypes).values);
        } catch (e) {
            toast.error(e.message);
        }
    };

    const handleConfirmationModalCancel = () => {
        setConfirmationModalOpen(false);
        setCaseTypes(previousCaseTypes);
    };

    const handleConfirmationModalConfirm = () => {
        setConfirmationModalOpen(false);
    };

    return (
        <>
            <Modal open>
                <Modal.Header content="Create Health Surveillance/Fit for Work Case" />
                <Modal.Content>
                    <Form>
                        <Input.Dropdown
                            required
                            value={caseTypes}
                            showErrors={submitted}
                            label="Case type"
                            options={caseTypeOptions}
                            onChange={onChangeCaseType}
                        />
                    </Form>
                </Modal.Content>
                <Modal.Actions>
                    <Button content="Cancel" onClick={props.navigateToParent} />
                    <IsLoadingButton requestNames={CREATE_HEALTH_SURVEILLANCE_CASE} content="Create" onClick={handleCreateHealthSurveillanceCase} />
                </Modal.Actions>
            </Modal>
            <Confirm
                open={confirmationModalOpen}
                // eslint-disable-next-line max-len
                content="Changing the case type may result in only some of the medicals associated with the work group being created - are you sure you wish to continue?"
                onCancel={handleConfirmationModalCancel}
                onConfirm={handleConfirmationModalConfirm}
            />
        </>
    );
};

const mapStateToProps = (state: ApplicationState): StateProps => ({
    customerId: customerIdSelector(state),
    employeeRecordId: employeeRecordIdSelector(state)
});

const mapDispatchToProps = (dispatch: ThunkDispatch<ApplicationState, undefined, any>): DispatchProps => ({
    navigateToParent: () => dispatch(navigateToParentRoute()),
    createHealthSurveillanceCase: async (customerId, employeeRecordId, caseTypes) => {
        const newCase = await dispatch<Promise<OhCaseDetailModel>>(createHealthSurveillanceCase(
            customerId, employeeRecordId, { healthSurveillanceCaseTypes: caseTypes }));
        dispatch(push(`/customers/${customerId}/employeeRecords/${employeeRecordId}/ohCases/${newCase.id}`));
    }
});

const mergeProps = (stateProps: StateProps, dispatchProps: DispatchProps): Props => ({
    navigateToParent: dispatchProps.navigateToParent,
    createHealthSurveillanceCase: (caseTypes) => dispatchProps.createHealthSurveillanceCase(stateProps.customerId, stateProps.employeeRecordId, caseTypes)
});

const CreateHealthSurveillanceCaseModal = connect(mapStateToProps, mapDispatchToProps, mergeProps)(CreateHealthSurveillanceCaseModalUnconnected);

export { CreateHealthSurveillanceCaseModal };
